/** @jsxImportSource @emotion/react */
import { useMemo } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import Markdown, { type Options as MarkdownOptions } from 'react-markdown';
import remarkFlexibleMarkers from 'remark-flexible-markers';
import remarkGfm from 'remark-gfm';
import { ScrollableTable } from '@trinity/chat-components/components/Markdown/ScrollableTable';

type MessageResponseMarkdownProps = {
  value: string;
};

export const MessageResponseMarkdown = ({ value }: MessageResponseMarkdownProps) => {
  const components = useMarkdownComponents();

  return (
    <Markdown
      css={markdownCss}
      remarkPlugins={[remarkGfm, remarkFlexibleMarkers]}
      components={components}>
      {value}
    </Markdown>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export function useMarkdownComponents(): MarkdownOptions['components'] {
  return useMemo(() => ({
    p: props => (
      <StyledP>
        {props.children}
      </StyledP>
    ),
    a: props => (
      <StyledA
        href={props.href}
        target="_blank"
        rel="noopener">
        {props.children}
      </StyledA>
    ),
    ul: props => (
      <StyledUl>
        {props.children}
      </StyledUl>
    ),
    li: props => (
      <StyledLi>
        {props.children}
      </StyledLi>
    ),
    ol: props => (
      <StyledOL>
        {props.children}
      </StyledOL>
    ),
    strong: props => (
      <StyledStrong>
        {props.children}
      </StyledStrong>
    ),
    h1: props => (
      <StyledH1>
        {props.children}
      </StyledH1>
    ),
    h2: props => (
      <StyledH2>
        {props.children}
      </StyledH2>
    ),
    h3: props => (
      <StyledH3>
        {props.children}
      </StyledH3>
    ),
    table: props => {
      return (
        <ScrollableTable>
          {props.children}
        </ScrollableTable>
      );
    },
    tr: props => (
      <StyledTr>
        {props.children}
      </StyledTr>
    ),
    td: props => (
      <StyledTd>
        {props.children}
      </StyledTd>
    ),
    th: props => (
      <StyledTh>
        {props.children}
      </StyledTh>
    ),
    mark: props => (
      <StyledMark>
        {props.children}
      </StyledMark>
    ),
  }), []);
}

const markdownCss = css({
  whiteSpace: 'normal',
});

const StyledMark = styled.mark({
  backgroundColor: 'yellow',
  borderRadius: 3,
});

const StyledStrong = styled.strong(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
}));

const StyledP = styled.p({
  margin: 0,
});

const StyledOL = styled.ol({
  paddingInlineStart: 16,
});

const StyledA = styled.a(({ theme }) => ({
  cursor: 'pointer',
  textDecoration: 'underline',
  fontSize: 16,

  ':hover': {
    color: theme.palette.primary.main,
  },
}));

const StyledUl = styled.ul({
  paddingInlineStart: 16,
});

const StyledLi = styled.li(({ theme }) => ({
  marginTop: 8,
  marginBottom: 8,

  '&::last-child': {
    marginBottom: 0,
  },

  '::marker': {
    fontFamily: theme.fonts.semiBold,
  },
}));

const StyledH1 = styled.h1(({ theme }) => ({
  fontFamily: theme.fonts.bold,
  fontSize: 18,
  marginTop: 22,
  marginBottom: 10,

  '&:first-child': {
    marginTop: 0,
  },
}));

const StyledH2 = styled.h2(({ theme }) => ({
  fontFamily: theme.fonts.bold,
  fontSize: 16,
  marginTop: 22,
  marginBottom: 10,

  '&:first-child': {
    marginTop: 0,
  },
}));

const StyledH3 = styled.h3(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  fontSize: 16,
  marginTop: 18,
  marginBottom: 6,

  '&:first-child': {
    marginTop: 0,
  },
}));

const StyledTr = styled.tr({
  backgroundColor: '#ffffff',
  borderTop: `1px solid hsla(210, 18%, 87%, 1)`,

  ':nth-child(2n)': {
    backgroundColor: '#f6f8fa',
  },
});

const StyledTd = styled.td({
  padding: '6px 13px',
  border: `1px solid #d0d7de`,
});

const StyledTh = styled.th(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  padding: '6px 13px',
  border: `1px solid #d0d7de`,
}));